import { useState } from 'react';
import type {
  ArenaTaskDto,
  ArenaRunMode,
  ArenaDifficulty,
  ArenaTaskStatus,
  ArenaScenarioInfo,
} from './types';

const STATUS_ICON: Record<ArenaTaskStatus, string> = {
  ready: '\u{1F7E1}',
  running: '\u{1F535}',
  passed: '\u{1F7E2}',
  failed: '\u{1F534}',
  error: '⚠️',
  completed: '✅',
};

const DIFFICULTY_BADGE: Record<ArenaDifficulty, string> = {
  basic: 'bg-green-100 text-green-700',
  intermediate: 'bg-amber-100 text-amber-700',
  advanced: 'bg-red-100 text-red-700',
};

interface TaskListProps {
  tasks: ArenaTaskDto[];
  selectedTaskId: string | null;
  onSelect: (task: ArenaTaskDto) => void;
  onRun: (taskId: string, mode: ArenaRunMode) => void;
  onApprove: (taskId: string, runId: string) => void;
  loading: boolean;
}

export default function TaskList({
  tasks,
  selectedTaskId,
  onSelect,
  onRun,
  onApprove,
  loading,
}: TaskListProps) {
  const [modeMap, setModeMap] = useState<Record<string, ArenaRunMode>>({});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm">
        No arena tasks yet. Create one from the scenarios tab.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {tasks.map(task => (
        <TaskCard
          key={task.id}
          task={task}
          selected={task.id === selectedTaskId}
          mode={modeMap[task.id] ?? (task.difficulty === 'basic' ? 'ui' : 'api')}
          onModeChange={(m) => setModeMap(prev => ({ ...prev, [task.id]: m }))}
          onSelect={() => onSelect(task)}
          onRun={(m) => onRun(task.id, m)}
          onApprove={() => task.lastRunId && onApprove(task.id, task.lastRunId)}
        />
      ))}
    </div>
  );
}

function TaskCard({
  task, selected, mode, onModeChange, onSelect, onRun, onApprove,
}: {
  task: ArenaTaskDto;
  selected: boolean;
  mode: ArenaRunMode;
  onModeChange: (mode: ArenaRunMode) => void;
  onSelect: () => void;
  onRun: (mode: ArenaRunMode) => void;
  onApprove: () => void;
}) {
  const isRunning = task.status === 'running';
  const isPassed = task.status === 'passed' || task.status === 'completed';
  const isFailed = task.status === 'failed' || task.status === 'error';
  const hasRuns = task.totalRuns > 0;

  return (
    <div
      onClick={onSelect}
      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
        selected ? 'border-primary-300 bg-primary-50 shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start gap-2">
        <span className="text-sm mt-0.5">{STATUS_ICON[task.status]}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">{task.name}</div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${DIFFICULTY_BADGE[task.difficulty]}`}>
              {task.difficulty}
            </span>
            <span className="text-xs text-gray-400">{task.category}</span>
            {hasRuns && (
              <span className={`text-xs font-medium ${isPassed ? 'text-green-600' : isFailed ? 'text-red-600' : 'text-gray-500'}`}>
                {task.passedRuns}/{task.totalRuns} passed
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2 ml-6" onClick={(e) => e.stopPropagation()}>
        <select
          value={mode}
          onChange={(e) => onModeChange(e.target.value as ArenaRunMode)}
          disabled={isRunning}
          className="px-2 py-0.5 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
        >
          <option value="ui">Mode: UI</option>
          <option value="api">Mode: API</option>
        </select>

        {isRunning ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Running...
          </span>
        ) : isPassed && task.lastRunId ? (
          <button onClick={onApprove} className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100">
            Approve
          </button>
        ) : (
          <button onClick={() => onRun(mode)} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
            Run
          </button>
        )}

        {isFailed && task.lastRunId && (
          <button onClick={() => onRun(mode)} className="px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100" title="Retry">
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

export function ScenarioCard({ scenario, onCreate }: { scenario: ArenaScenarioInfo; onCreate: (scenarioId: string) => void }) {
  return (
    <div className="p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">{scenario.name}</div>
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{scenario.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${DIFFICULTY_BADGE[scenario.difficulty]}`}>
              {scenario.difficulty}
            </span>
            <span className="text-xs text-gray-400">{scenario.category}</span>
          </div>
        </div>
        <button onClick={() => onCreate(scenario.id)} className="shrink-0 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100 transition-colors">
          Create
        </button>
      </div>
    </div>
  );
}
